import type { FC } from 'react';

import { useCallback, useEffect, useRef, useState } from 'react';

import Webcam from 'react-webcam';

import { REACT_APP_CDN_URL } from 'envs';

import { useHeadTurn } from './use-head-turn';

const FRAME_COUNT = 3;
const FRAME_GAP_MS = 300;
const DATA_URL_PREFIX = /^data:image\/\w+;base64,/;
const CAMERA_ERROR =
	'Could not access the camera. Check its permission and try again.';
const NOT_READY_ERROR = 'The camera was not ready — try again.';

/* The same illustration the applicant-facing enrolment shows (kyc.frontend
   `LeftRight_URL`), off the public asset CDN admin.panel already reads from. */
const ILLUSTRATION_URL = `${REACT_APP_CDN_URL}/assets/illustrations/simplici-media/LeftRight.mp4`;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/** What the caller's enrolment call came back with. */
export type IFaceCaptureOutcome =
	| { ok: true; token: string }
	| { ok: false; message: string };

export interface IFaceVerifyProps {
	/** Enrols the burst for the pending transfer; the caller owns the transfer id. */
	onCaptured: (images: string[]) => Promise<IFaceCaptureOutcome>;
	/** The user acknowledged the verified screen — advance to the fee. */
	onContinue: (token: string) => void;
	busy: boolean;
}

type IScreen = 'intro' | 'capture' | 'review' | 'verified';

/**
 * The `verify` step's panel — the applicant enrolment's PROCESS, on the v2
 * design system.
 *
 * Four screens, the same four the applicant-facing flow walks
 * (kyc.frontend `views/facial-enroll`): an intro with the capture tips, the
 * camera with a face guide, a review of the photo with Submit/Retake, and a
 * verified confirmation. Before this it was one bare screen — a sentence, a
 * preview box and a Capture button — which worked but looked and read like
 * nothing else in the product, and gave no chance to check the photo before
 * it was enrolled as the record of who authorised the transfer.
 *
 * THE HEAD-TURN GESTURE. Capture is gated on the same right-then-left turn
 * the applicant flow asks for, decided by the same MediaPipe blendshape
 * heuristic with the same thresholds (`use-head-turn.ts`). It is a CAPTURE
 * GATE run in the browser, not a liveness verdict: nothing about it is sent
 * or stored, and the transfer's proof remains the enrolment biometrics.api
 * makes from the frames. The design spec (2026-09-10 §2) had deferred it as a
 * follow-up; the product owner asked for it, so it is here — with the
 * applicant flow's own fallback, which lets the capture proceed (and says so)
 * if the landmarker cannot load. The guide is a rectangle, by request.
 *
 * WHAT IS DELIBERATELY NOT HERE, AND WHY.
 *
 * The "complete on your smartphone" QR. The applicant flow's QR hands an
 * ONBOARDING SESSION to a phone (`useQrPage(QrVerification.Auth)`); there is
 * no transfer-scoped mobile route for that QR to open, so drawing one would be
 * a code that leads nowhere. It needs a real handoff first.
 *
 * MIRRORING. The preview and the review still are flipped in CSS only
 * (`.fvcam video`, `.fvshot img`), never via react-webcam's `mirrored`, which
 * also flips the screenshot. The frames sent to biometrics.api are therefore
 * byte-for-byte what the previous version sent — the user sees a mirror,
 * the enrolment does not.
 *
 * Renders as the modal's ordinary children, so it inherits `.mdlb`'s padding
 * without a wrapper of its own.
 */
export const FaceVerify: FC<IFaceVerifyProps> = ({
	onCaptured,
	onContinue,
	busy,
}) => {
	const [screen, setScreen] = useState<IScreen>('intro');
	const webcamRef = useRef<Webcam>(null);
	const [cameraError, setCameraError] = useState('');
	/* `getScreenshot()` returns null until react-webcam's own `hasUserMedia` is
	   true (and the video has real dimensions) — gate Capture on the same
	   signal rather than letting an instant click send blank frames. */
	const [ready, setReady] = useState(false);
	/* react-webcam's underlying element, handed to the head-turn hook once the
	   stream is up. Null whenever the camera screen is not showing, which is
	   what stops inference. */
	const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
	const [capturing, setCapturing] = useState(false);
	/* Declared before `capture`, which reads `passed` to gate the burst. */
	const headTurn = useHeadTurn(videoEl, screen === 'capture' && ready && !cameraError);
	/* The burst, kept in both forms: the first frame as a data URL for the
	   review still, and all three stripped to base64 for the enrolment. */
	const [preview, setPreview] = useState('');
	const [frames, setFrames] = useState<string[]>([]);
	/* Two failures, two homes. A short burst never reaches the caller (nothing
	   to verify) and is shown on the capture screen; an enrolment refusal
	   comes back from the caller and is shown on the review screen, under the
	   photo it refused. */
	const [captureError, setCaptureError] = useState('');
	const [submitError, setSubmitError] = useState('');
	const [token, setToken] = useState('');
	const [illustrationFailed, setIllustrationFailed] = useState(false);
	/* The burst spans ~600ms of real time (3 frames, 300ms apart) during which
	   the reader can close the modal — ×, backdrop, Escape all cancel the
	   pending transfer immediately (transfer-modal.tsx) and unmount this
	   component. Finishing the burst afterwards must not set state on an
	   unmounted component. */
	const unmounted = useRef(false);
	useEffect(
		() => () => {
			unmounted.current = true;
		},
		[]
	);

	const begin = useCallback(() => {
		setCameraError('');
		setReady(false);
		setVideoEl(null);
		setCaptureError('');
		setScreen('capture');
	}, []);

	const capture = useCallback(async () => {
		if (busy || capturing || !ready || !headTurn.passed) return;
		setCapturing(true);
		setCaptureError('');
		const shots: string[] = [];
		for (let index = 0; index < FRAME_COUNT; index += 1) {
			const shot = webcamRef.current?.getScreenshot();
			if (shot) shots.push(shot);
			if (index < FRAME_COUNT - 1) await sleep(FRAME_GAP_MS);
		}
		if (unmounted.current) return;
		setCapturing(false);
		if (shots.length < FRAME_COUNT) {
			setCaptureError(NOT_READY_ERROR);
			return;
		}
		/* The length check above guarantees a frame; `?? ''` is for the type only. */
		setPreview(shots[0] ?? '');
		setFrames(shots.map(shot => shot.replace(DATA_URL_PREFIX, '')));
		setSubmitError('');
		setScreen('review');
	}, [busy, capturing, headTurn.passed, ready]);

	const submit = useCallback(async () => {
		if (busy || !frames.length) return;
		setSubmitError('');
		const outcome = await onCaptured(frames);
		if (unmounted.current) return;
		if (outcome.ok) {
			setToken(outcome.token);
			setScreen('verified');
			return;
		}
		setSubmitError(outcome.message);
	}, [busy, frames, onCaptured]);

	const retake = useCallback(() => {
		if (busy) return;
		setPreview('');
		setFrames([]);
		setSubmitError('');
		begin();
	}, [begin, busy]);

	const retryCamera = useCallback(() => {
		setCameraError('');
		setReady(false);
		setVideoEl(null);
	}, []);


	if (screen === 'intro') {
		return (
			<div className="facev">
				<div className="fvcols">
					<div className="fvcopy">
						<div className="fvhead">Facial enrollment</div>
						<p className="fvbody">
							To confirm it&apos;s you before the originals move, we enrol your
							face for this transfer. It is kept only as the record of who
							authorised it.
						</p>

						<div className="fvtips">
							<div className="fvtips__t">
								<span aria-hidden="true">⚡</span> Tips for a good capture
							</div>
							<ul>
								<li>Find a well-lit area — light on your face, not behind you.</li>
								<li>Position your face within the frame.</li>
								<li>Turn your head slowly to the right, then to the left, and come back to the centre.</li>
								<li>Look straight at the camera and stay still while the photo is taken.</li>
							</ul>
						</div>

						<button
							className="dbtn pri fvcta"
							type="button"
							disabled={busy}
							onClick={begin}
						>
							Begin facial enrollment
						</button>
					</div>

					{/* Decorative: the same clip the applicant flow shows. If the CDN
					    is unreachable the column collapses to a quiet tile rather than
					    a broken player. */}
					<div className="fvillus" aria-hidden="true">
						{illustrationFailed ? (
							<div className="fvillus__blank" />
						) : (
							<video
								src={ILLUSTRATION_URL}
								muted
								autoPlay
								loop
								playsInline
								onError={() => setIllustrationFailed(true)}
							/>
						)}
					</div>
				</div>
			</div>
		);
	}

	if (screen === 'capture') {
		const disabled = busy || capturing || !ready || !headTurn.passed;
		const label = !ready
			? 'Starting camera…'
			: capturing
			? 'Capturing…'
			: captureError
			? 'Try again'
			: 'Capture';
		/* One instruction at a time, in the order the gesture happens. */
		const cue = !ready
			? 'Starting your camera…'
			: headTurn.status === 'loading'
			? 'Loading the head-turn check…'
			: headTurn.status === 'bypassed'
			? 'Look straight at the camera and capture.'
			: !headTurn.faceVisible
			? 'Position your face within the frame.'
			: headTurn.phase === 'right'
			? 'Slowly turn your head to the right →'
			: headTurn.phase === 'left'
			? '← Now, turn your head to the left'
			: headTurn.phase === 'straight'
			? 'Come back to the centre and look straight at the camera.'
			: 'Ready — hold still and capture.';
		const turnClass = (done: boolean, now: boolean) =>
			`fvturn${done ? ' ok' : now ? ' now' : ''}`;

		return (
			<div className="facev">
				<div className="fvhead">Facial enrollment</div>

				<div className="fvcam">
					{cameraError ? (
						<div className="fvcamerr">{cameraError}</div>
					) : (
						<>
							<Webcam
								ref={webcamRef}
								audio={false}
								screenshotFormat="image/jpeg"
								videoConstraints={{ facingMode: 'user', width: 640, height: 480 }}
								onUserMedia={() => {
									setReady(true);
									setVideoEl(webcamRef.current?.video ?? null);
								}}
								onUserMediaError={() => {
									setCameraError(CAMERA_ERROR);
									setReady(false);
								}}
							/>
							{/* The face guide: everything outside the frame dimmed, the frame
							    itself ringed — blue while the gesture is in progress, green
							    once it is done. `evenodd` cuts the rounded rectangle out of
							    the full-frame rect in one path, so the mask needs no filter. */}
							<svg
								className={`fvguide${headTurn.passed ? ' ok' : ''}`}
								viewBox="0 0 640 480"
								preserveAspectRatio="xMidYMid slice"
								aria-hidden="true"
							>
								<path
									className="fvguide__dim"
									fillRule="evenodd"
									d="M0 0H640V480H0Z M150 40H490A24 24 0 0 1 514 64V416A24 24 0 0 1 490 440H150A24 24 0 0 1 126 416V64A24 24 0 0 1 150 40Z"
								/>
								<rect className="fvguide__ring" x="126" y="40" width="388" height="400" rx="24" />
							</svg>
							<div className="fvcue" role="status" aria-live="polite">
								{cue}
							</div>
						</>
					)}
				</div>

				{/* The three parts of the gesture, ticked as they land — the v1
				    screens' green success markers, as chips. */}
				{!cameraError && (
					<div className="fvturns" aria-label="Head-turn progress">
						<span className={turnClass(headTurn.rightDone, headTurn.phase === 'right')}>
							Turn right
						</span>
						<span className={turnClass(headTurn.leftDone, headTurn.phase === 'left')}>
							Turn left
						</span>
						<span className={turnClass(headTurn.passed, headTurn.phase === 'straight')}>
							Look straight
						</span>
					</div>
				)}

				{headTurn.status === 'bypassed' && (
					<div className="fvbypass">
						The head-turn check could not load, so this capture proceeds without it.
					</div>
				)}

				{!!captureError && <div className="merr">{captureError}</div>}

				<div className="fvacts">
					{cameraError ? (
						<button className="dbtn" type="button" onClick={retryCamera}>
							Try again
						</button>
					) : (
						<button
							className="dbtn pri fvcta"
							type="button"
							disabled={disabled}
							onClick={() => void capture()}
						>
							{label}
						</button>
					)}
				</div>
			</div>
		);
	}

	if (screen === 'review') {
		return (
			<div className="facev">
				<div className="fvhead">Well done — here&apos;s your photo</div>
				<p className="fvbody">
					Check that your face is clearly visible, then submit it. Otherwise,
					retake the photo.
				</p>

				<div className="fvshot">
					<img src={preview} alt="Your captured photo" />
				</div>

				{!!submitError && <div className="merr">{submitError}</div>}

				<div className="fvacts">
					<button className="dbtn" type="button" disabled={busy} onClick={retake}>
						Retake
					</button>
					<button
						className="dbtn pri fvcta"
						type="button"
						disabled={busy}
						onClick={() => void submit()}
					>
						{busy ? 'Submitting…' : submitError ? 'Try again' : 'Submit'}
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="facev">
			<div className="fvok">
				<div className="fvcheck" aria-hidden="true">
					✓
				</div>
				<div className="fvhead">Face verified</div>
				<p className="fvbody">
					Your face has been enrolled for this transfer only. It is kept as the
					record of who authorised it.
				</p>
				<button
					className="dbtn pri fvcta"
					type="button"
					onClick={() => onContinue(token)}
				>
					Continue
				</button>
			</div>
		</div>
	);
};
